import {
    Box,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Flex,
    HStack,
    useDisclosure,
    Checkbox,
    Alert,
    AlertIcon,
    AlertDescription,
    IconButton,
    Icon,
    Tooltip,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Button,
    Textarea,
    VStack,
    Wrap,
    WrapItem,
    Tag,
    Badge,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Radio,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Portal,
} from '@chakra-ui/react';
import { QuotePart, OrderEmsSummary } from '../../hooks/useGetOrderDetail';
import { formatPrice } from '../../functions';
import PartType from '../PartType';
import PartNumber from '../PartNumber';
import SubmitQuoteModal from '../../modals/SubmitQuoteModal';
import LocationMismatchWarningModal from '../../modals/LocationMismatchWarningModal';
import { useBulkToggleQuotingExclusion, useTogglePartExclusion } from '../../hooks/useTogglePartExclusion';
import { useTogglePartFlag } from '../../hooks/useTogglePartFlag';
import { useCreateAssemblyMapping } from '../../hooks/useCreateAssemblyMapping';
import { useDeleteAssemblyMapping } from '../../hooks/useDeleteAssemblyMapping';
import { useGetOrderWorkflowStatus } from '../../hooks/useGetOrderWorkflowStatus';
import { useIsOverflowing } from '../../hooks/useIsOverflowing';
import React, { useMemo, useState, useContext, useRef } from 'react';
import PartBayButton from '../PartBayButton';
import { TbAlertTriangle, TbCheck, TbFileDescription, TbFlag, TbStack2, TbTrash } from 'react-icons/tb';
import { Icon as YaxleIcon, IconButton as YaxleIconButton } from '../../ui';
import SessionContext from '../../contexts/SessionContext';
import { ManualFlagType } from '../../hooks/useTogglePartFlag';

interface AssemblyGroup {
    parent: QuotePart;
    parts: Array<QuotePart>;
}

const MANUAL_FLAG_TYPE_QUICK_SELECT: Array<{ value: ManualFlagType; label: string; }> = [
    { value: 'head-tail-lamps-with-logo', label: 'Request OEM: Head/tail lamps or mirrors with logo' },
    { value: 'oe-warranty', label: 'Request OEM: Warranty part' },
    { value: 'welded-on-parts', label: 'Request OEM: Welded-on part' },
    { value: 'request-oem-one-time-use-part', label: 'Exclude RCY: One time use' },
    { value: 'exclude-lkq-parts', label: 'Exclude RCY: Recycled parts invalid' },
    { value: 'shop-loss', label: 'Shop Loss: Price at cost' },
    { value: 'promo-pricing', label: 'CCC Promo Pricing' },
];

interface FlagPartPopoverProps {
    isDisabled: boolean;
    onSubmit: (reason: string, manualFlagType?: ManualFlagType) => void;
}

const FlagPartPopover = ({ isDisabled, onSubmit }: FlagPartPopoverProps) => {
    const [flagReason, setFlagReason] = useState('');
    const [manualFlagType, setManualFlagType] = useState<ManualFlagType | undefined>(undefined);

    const reset = () => {
        setFlagReason('');
        setManualFlagType(undefined);
    };

    return (
        <Popover placement="top" onClose={reset}>
            {({ onClose: closePopover }) => (
                <>
                    <PopoverTrigger>
                        <Box>
                            <Tooltip label="Flag Part" placement="top" hasArrow>
                                <IconButton
                                    aria-label="Toggle flag"
                                    icon={<Icon as={TbFlag as React.ElementType} w={5} h={5} fill="none" />}
                                    size="sm"
                                    variant="ghost"
                                    color="gray.400"
                                    _hover={{
                                        bg: 'transparent',
                                        color: "orange.400"
                                    }}
                                    isDisabled={isDisabled}
                                />
                            </Tooltip>
                        </Box>
                    </PopoverTrigger>
                    <PopoverContent width="350px" boxShadow="none">
                        <PopoverBody>
                            <VStack align="stretch" spacing={3}>
                                <Box mt={2}>
                                    <Wrap spacing={2}>
                                        {MANUAL_FLAG_TYPE_QUICK_SELECT.map((option) => {
                                            const isSelected = manualFlagType === option.value;
                                            return (
                                                <WrapItem key={option.value}>
                                                    <Tag
                                                        size="sm"
                                                        variant={isSelected ? "solid" : "subtle"}
                                                        colorScheme="orange"
                                                        textTransform="none"
                                                        fontWeight="medium"
                                                        letterSpacing="normal"
                                                        cursor="pointer"
                                                        _hover={isSelected ? undefined : { bg: "orange.100", color: "orange.800" }}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setManualFlagType(undefined);
                                                                setFlagReason((current) => current === option.label ? '' : current);
                                                            } else {
                                                                setManualFlagType(option.value);
                                                                setFlagReason(option.label);
                                                            }
                                                        }}
                                                        transition="all 0.2s"
                                                    >
                                                        {option.label}
                                                    </Tag>
                                                </WrapItem>
                                            );
                                        })}
                                    </Wrap>
                                </Box>
                                <Textarea
                                    value={flagReason}
                                    onChange={(e) => setFlagReason(e.target.value)}
                                    placeholder='Enter another reason...'
                                    size="sm"
                                    rows={3}
                                    isReadOnly={!!manualFlagType}
                                    isDisabled={!!manualFlagType}
                                />
                                {manualFlagType && (
                                    <Text fontSize="xs" color="gray.500">
                                        Deselect the quick-select option above to enter a custom reason.
                                    </Text>
                                )}
                                <HStack justify="flex-end" spacing={2}>
                                    <Button size="sm" variant="outlineGray" onClick={closePopover}>
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        colorScheme="orange"
                                        onClick={() => {
                                            onSubmit(flagReason, manualFlagType);
                                            reset();
                                            closePopover();
                                        }}
                                    >
                                        Flag
                                    </Button>
                                </HStack>
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </>
            )}
        </Popover>
    );
};

interface NewPartsSectionProps {
    parts: Array<QuotePart>;
    orderId: string;
    orderLocationId: string;
    orderLocationName: string;
    vin: string;
    ems: OrderEmsSummary;
    onOpenExportHistory: () => void;
    refetch: () => void | Promise<unknown>;
}

const NewPartsSection = ({
    parts,
    orderId,
    orderLocationId,
    orderLocationName,
    vin,
    ems,
    onOpenExportHistory,
    refetch,
}: NewPartsSectionProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isLocationWarningOpen,
        onOpen: onLocationWarningOpen,
        onClose: onLocationWarningClose
    } = useDisclosure();
    const sessionContext = useContext(SessionContext);

    const { data: workflowStatus } = useGetOrderWorkflowStatus(orderId, 10 * 1000);
    const isQuoteWorkflowBusy = useMemo(() => {
        const items = [...(workflowStatus?.inProgress ?? []), ...(workflowStatus?.pending ?? [])];
        return items.some((item) => item.key === 'request-quotes' || item.key === 'fetching-part-options');
    }, [workflowStatus]);

    const [pendingInclude, setPendingInclude] = useState<{ part: QuotePart; action?: 'exclude-quoting'; } | null>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    const {
        isOpen: isDuplicateConfirmOpen,
        onOpen: onDuplicateConfirmOpen,
        onClose: onDuplicateConfirmClose
    } = useDisclosure();
    const {
        isOpen: isQuotingPopupOpen,
        onOpen: onQuotingPopupOpen,
        onClose: onQuotingPopupClose
    } = useDisclosure();
    const {
        isOpen: isMappingsOpen,
        onOpen: onMappingsOpen,
        onClose: onMappingsClose
    } = useDisclosure();

    const toggleExclusion = useTogglePartExclusion();
    const bulkToggleExclusion = useBulkToggleQuotingExclusion();
    const togglePartFlag = useTogglePartFlag();
    const quotingPopupCloseRef = useRef<HTMLButtonElement>(null);

    const numNewParts = useMemo(() => (
        parts.reduce((tot, p) => (
            p.status === 'pre-quoting' && !p.excludeQuoting ? tot + 1 : tot
        ), 0)
    ), [parts]);

    const assemblyGroups = useMemo((): Array<AssemblyGroup> => {
        const byId = new Map(parts.map((p) => [p._id, p]));
        return parts
            .filter((p) => p.assemblySubParts && p.assemblySubParts.length > 0)
            .map((parent) => {
                const children = (parent.assemblySubParts ?? [])
                    .map((id) => byId.get(id))
                    .filter((p): p is QuotePart => !!p);
                return { parent, parts: [parent, ...children] };
            });
    }, [parts]);

    const isPartExcluded = (part: QuotePart) => Boolean(part.excludeQuoting || part.quoteAndExclude);

    const allPartsExcluded = parts.length > 0 && parts.every(isPartExcluded);
    const somePartsExcluded = parts.some(isPartExcluded);

    const handleExclusionToggle = (part: QuotePart) => {
        // If the part is currently excluded AND is a duplicate, show confirmation before including
        if (part.excludeQuoting && part.isDuplicate) {
            setPendingInclude({ part });
            onDuplicateConfirmOpen();
        } else {
            // No confirmation needed - toggle directly
            toggleExclusion.mutate({
                orderPartId: part._id,
                orderId: orderId,
                origin: 'quoting'
            }, {
                onSuccess: ({ data }) => {
                    if (data.showQuotingPopup) {
                        onQuotingPopupOpen();
                    }
                }
            });
        }
    };

    const handleQuotingExclusionToggle = (part: QuotePart) => {
        if (part.excludeQuoting && part.isDuplicate) {
            setPendingInclude({ part, action: 'exclude-quoting' });
            onDuplicateConfirmOpen();
            return;
        }
        toggleExclusion.mutate({
            orderPartId: part._id,
            orderId: orderId,
            origin: 'quoting',
            action: 'exclude-quoting'
        });
    };

    const handleExcludeAllToggle = () => {
        if (bulkToggleExclusion.isPending || parts.length === 0) return;

        bulkToggleExclusion.mutate({
            orderId,
            parts,
            exclude: !allPartsExcluded,
        }, {
            onSuccess: ({ showQuotingPopup }) => {
                if (showQuotingPopup) {
                    onQuotingPopupOpen();
                }
            },
        });
    };

    const handleConfirmIncludeDuplicate = () => {
        if (pendingInclude) {
            toggleExclusion.mutate({
                orderPartId: pendingInclude.part._id,
                orderId: orderId,
                origin: 'quoting',
                ...(pendingInclude.action && { action: pendingInclude.action })
            }, {
                onSuccess: ({ data }) => {
                    if (data.showQuotingPopup) {
                        onQuotingPopupOpen();
                    }
                }
            });
        }
        setPendingInclude(null);
        onDuplicateConfirmClose();
    };

    const handleCancelIncludeDuplicate = () => {
        setPendingInclude(null);
        onDuplicateConfirmClose();
    };

    const handleFlagToggle = (partId: string, reason?: string, manualFlagType?: ManualFlagType) => {
        togglePartFlag.mutate({
            orderPartId: partId,
            orderId: orderId,
            ...(reason && { flagReason: reason }),
            ...(manualFlagType && { manualFlagType }),
        });
    };

    const handleRequestPartsClick = () => {
        const showLocationWarning =
            sessionContext?.isAuthenticated &&
            sessionContext.locationId &&
            sessionContext.locationId !== 'all' &&
            !sessionContext.locationId.includes(orderLocationId);

        if (showLocationWarning) {
            onLocationWarningOpen();
        } else {
            onOpen();
        }
    };

    // EMS status indicator on the Export History icon. Submission status of the
    // most recent export takes priority so customers don't miss parts from a newly
    // exported estimate that hasn't been sent to marketplaces yet.
    const latestEmsPush = ems.pushes.find(push => push.isLatest);
    const latestEmsPushUnsubmitted = latestEmsPush ? !latestEmsPush.wasSubmitted : false;
    const emsIndicator = latestEmsPushUnsubmitted
        ? {
            label: 'Not sent',
            dot: 'red.500',
            icon: TbAlertTriangle,
            color: 'red.600',
            borderColor: 'red.200',
            bg: 'red.50',
            hoverBg: 'red.100',
            tooltip: 'The most recent EMS export has not been sent to marketplaces yet. New parts from this estimate may be missing — open Export History to review and submit it.',
        }
        : !ems.matchesLatestEmsPrices
            ? {
                label: 'Price mismatch',
                dot: 'orange.400',
                icon: TbAlertTriangle,
                color: 'orange.600',
                borderColor: 'orange.200',
                bg: 'orange.50',
                hoverBg: 'orange.100',
                tooltip: 'New Parts estimated list prices do not match the latest EMS. Sync to update them before quoting.',
            }
            : {
                label: 'Up to date',
                dot: 'green.500',
                icon: TbCheck,
                color: 'green.600',
                borderColor: 'green.200',
                bg: 'green.50',
                hoverBg: 'green.100',
                tooltip: 'New Parts match the latest EMS export, which has been sent to marketplaces.',
            };

    return (
        <>
            <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                {/* Header */}
                <Flex justify="space-between" align="center" px={6} pt={5} pb={4}>
                    <HStack spacing={2} align="center">
                        <Heading size="md" color="gray.800">
                            New Parts
                        </Heading>
                        {ems.currentEmsId && (
                            <Tooltip label={emsIndicator.tooltip} placement="top" hasArrow openDelay={200}>
                                <Button
                                    onClick={onOpenExportHistory}
                                    size="sm"
                                    variant="outline"
                                    borderWidth="1px"
                                    borderColor={emsIndicator.borderColor}
                                    bg={emsIndicator.bg}
                                    color={emsIndicator.color}
                                    fontWeight="medium"
                                    _hover={{ bg: emsIndicator.hoverBg }}
                                    _active={{ bg: emsIndicator.hoverBg }}
                                    iconSpacing={1}
                                    leftIcon={<Icon as={TbFileDescription as React.ElementType} boxSize={4} color="blue.500" />}
                                >
                                    <HStack spacing={1} align="center">
                                        <Icon as={emsIndicator.icon as React.ElementType} boxSize={3.5} color={emsIndicator.dot} />
                                        <Text>{emsIndicator.label}</Text>
                                    </HStack>
                                </Button>
                            </Tooltip>
                        )}
                    </HStack>
                    <HStack spacing={3}>
                        {parts.length > 0 && (
                            <>
                                <PartBayButton
                                    colorScheme='partbay-blue'
                                    variant='outline'
                                    size="sm"
                                    onClick={onMappingsOpen}
                                    text='Create Assembly'
                                    isDisabled={parts.length < 2}
                                />
                                <PartBayButton
                                    colorScheme='partbay-blue'
                                    variant='solid'
                                    size="sm"
                                    onClick={handleRequestPartsClick}
                                    text='Request Parts'
                                    isDisabled={numNewParts === 0 || isQuoteWorkflowBusy}
                                />
                            </>
                        )}
                    </HStack>
                </Flex>

                {parts.some(p => p.excludeQuoting) && <Box mb={4} px={6}>
                    <Alert
                        status="warning"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="orange.400"
                        bg="orange.50"
                    >
                        <AlertIcon color="orange.500" />
                        <AlertDescription color="gray.700" fontSize="sm">
                            Some parts will not be quoted at all. Click the quote icon to quote them again, or uncheck to fully include them.
                        </AlertDescription>
                    </Alert>
                </Box>}

                {assemblyGroups.length > 0 ? (
                    <Tabs variant="unstyled" colorScheme="blue">
                        <Flex
                            justify="space-between"
                            align="flex-end"
                            mb={4}
                            borderBottom="1.5px solid"
                            borderColor="gray.200"
                            px={6}
                        >
                            <TabList border="none" gap={0}>
                                <Tab
                                    fontWeight="medium"
                                    fontSize="sm"
                                    color="gray.500"
                                    bg="transparent"
                                    border="none"
                                    borderBottom="1.5px solid"
                                    borderColor="transparent"
                                    mb="-1.5px"
                                    py={2}
                                    px={3}
                                    _selected={{
                                        color: 'gray.800',
                                        borderColor: 'gray.500',
                                    }}
                                    _hover={{
                                        color: 'gray.700',
                                    }}
                                >
                                    All
                                </Tab>
                                <Tab
                                    fontWeight="medium"
                                    fontSize="sm"
                                    color="gray.500"
                                    bg="transparent"
                                    border="none"
                                    borderBottom="1.5px solid"
                                    borderColor="transparent"
                                    mb="-1.5px"
                                    py={2}
                                    px={3}
                                    _selected={{
                                        color: 'gray.800',
                                        borderColor: 'gray.500',
                                    }}
                                    _hover={{
                                        color: 'gray.700',
                                    }}
                                >
                                    Assemblies
                                </Tab>
                            </TabList>
                        </Flex>
                        <TabPanels>
                            <TabPanel p={0}>
                                <NewPartsTable
                                    parts={parts}
                                    vin={vin}
                                    orderId={orderId}
                                    allPartsExcluded={allPartsExcluded}
                                    somePartsExcluded={somePartsExcluded}
                                    isBulkTogglePending={bulkToggleExclusion.isPending}
                                    onExcludeAllToggle={handleExcludeAllToggle}
                                    onExclusionToggle={handleExclusionToggle}
                                    onQuotingExclusionToggle={handleQuotingExclusionToggle}
                                    onFlagToggle={handleFlagToggle}
                                    refetch={refetch}
                                />
                            </TabPanel>
                            <TabPanel p={0}>
                                <NewPartsTable
                                    parts={parts}
                                    groups={assemblyGroups}
                                    vin={vin}
                                    orderId={orderId}
                                    allPartsExcluded={allPartsExcluded}
                                    somePartsExcluded={somePartsExcluded}
                                    isBulkTogglePending={bulkToggleExclusion.isPending}
                                    onExcludeAllToggle={handleExcludeAllToggle}
                                    onExclusionToggle={handleExclusionToggle}
                                    onQuotingExclusionToggle={handleQuotingExclusionToggle}
                                    onFlagToggle={handleFlagToggle}
                                    refetch={refetch}
                                />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                ) : (
                    <Box borderTop="1px solid" borderColor="gray.200">
                        <NewPartsTable
                            parts={parts}
                            vin={vin}
                            orderId={orderId}
                            allPartsExcluded={allPartsExcluded}
                            somePartsExcluded={somePartsExcluded}
                            isBulkTogglePending={bulkToggleExclusion.isPending}
                            onExcludeAllToggle={handleExcludeAllToggle}
                            onExclusionToggle={handleExclusionToggle}
                            onQuotingExclusionToggle={handleQuotingExclusionToggle}
                            onFlagToggle={handleFlagToggle}
                            refetch={refetch}
                        />
                    </Box>
                )}
            </Box>
            <SubmitQuoteModal isOpen={isOpen} onClose={onClose} orderId={orderId} />
            {sessionContext?.isAuthenticated && sessionContext?.locationId && (
                <LocationMismatchWarningModal
                    isOpen={isLocationWarningOpen}
                    onClose={onLocationWarningClose}
                    orderLocationName={orderLocationName}
                    userLocationName={sessionContext.locationName}
                />
            )}

            {/* Duplicate Part Confirmation Dialog */}
            <AlertDialog
                isOpen={isDuplicateConfirmOpen}
                leastDestructiveRef={cancelRef}
                onClose={handleCancelIncludeDuplicate}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Include Duplicate Part?
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            This part has been flagged as a duplicate. Are you sure you want to include it for quoting?
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button size="action" variant="outlineGray" ref={cancelRef} onClick={handleCancelIncludeDuplicate}>
                                Cancel
                            </Button>
                            <Button
                                size="action"
                                bg="#0071E3"
                                color="white"
                                _hover={{ bg: '#3544a0' }}
                                onClick={handleConfirmIncludeDuplicate}
                                ml={3}
                            >
                                Yes, Include
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <QuotingChangeAlertDialog
                isOpen={isQuotingPopupOpen}
                onClose={onQuotingPopupClose}
                leastDestructiveRef={quotingPopupCloseRef}
            />

            <AssemblyMappingModal
                isOpen={isMappingsOpen}
                onClose={onMappingsClose}
                parts={parts}
                orderId={orderId}
                refetch={refetch}
            />
        </>
    );
};

interface QuotingChangeAlertDialogProps {
    isOpen: boolean;
    onClose: () => void;
    leastDestructiveRef: React.RefObject<HTMLButtonElement>;
}

const QuotingChangeAlertDialog = ({ isOpen, onClose, leastDestructiveRef }: QuotingChangeAlertDialogProps) => (
    <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={leastDestructiveRef}
        onClose={onClose}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
        size="xl"
    >
        <AlertDialogOverlay>
            <AlertDialogContent maxW="640px" overflow="hidden">
                <AlertDialogHeader
                    bg="gray.100"
                    borderBottom="1px solid"
                    borderColor="gray.300"
                    color="gray.800"
                    fontSize="xl"
                    fontWeight="semibold"
                    px={6}
                    py={4}
                >
                    Quoting Change
                </AlertDialogHeader>

                <AlertDialogBody px={6} py={5}>
                    <Text fontSize="md" color="gray.700" lineHeight="1.7">
                        You can no longer exclude parts with part numbers. We made this change to help quoting percentages for carriers. This will quote the part and exclude it from ordering. This applies to all orders, and you won't see this message again. Thanks!
                    </Text>
                </AlertDialogBody>

                <AlertDialogFooter
                    bg="gray.50"
                    borderTop="1px solid"
                    borderColor="gray.300"
                    px={6}
                    py={4}
                >
                    <Button
                        size="action"
                        ref={leastDestructiveRef}
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3141a5' }}
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialogOverlay>
    </AlertDialog>
);

interface NewPartsPartRowProps {
    part: QuotePart;
    index: number;
    vin: string;
    orderId: string;
    canFlagParts: boolean;
    canIncludeExclude: boolean;
    onExclusionToggle: (part: QuotePart) => void;
    onQuotingExclusionToggle: (part: QuotePart) => void;
    onFlagToggle: (partId: string, reason?: string, manualFlagType?: ManualFlagType) => void;
    refetch: () => void | Promise<unknown>;
}

const NewPartsPartRow = ({
    part,
    index,
    vin,
    orderId,
    canFlagParts,
    canIncludeExclude,
    onExclusionToggle,
    onQuotingExclusionToggle,
    onFlagToggle,
    refetch,
}: NewPartsPartRowProps) => (
    <Tr
        _hover={{ bg: 'gray.25' }}
        sx={{
            ...(part.excludeQuoting && {
                opacity: 0.5,
            }),
        }}
    >
        {(canFlagParts || canIncludeExclude) && <Td py={4} px={4}>
            <HStack spacing={2}>
                {canIncludeExclude && <Tooltip label={part.excludeQuoting ? 'Include Part' : 'Exclude Part'} placement="top" hasArrow>
                    <Box display="inline-block">
                        <Checkbox
                            key={`checkbox-${part._id}`}
                            isChecked={part.excludeQuoting || part.quoteAndExclude}
                            onChange={() => onExclusionToggle(part)}
                            size="md"
                            sx={{
                                '&[data-checked]': {
                                    bg: '#0071E3',
                                    borderColor: '#0071E3',
                                },
                                '&[data-checked]:hover': {
                                    bg: '#005BC4',
                                    borderColor: '#005BC4',
                                },
                            }}
                        />
                    </Box>
                </Tooltip>}
                {!part.flag && canFlagParts ? (
                    <FlagPartPopover
                        isDisabled={part.excludeQuoting}
                        onSubmit={(reason, manualFlagType) => onFlagToggle(part._id, reason, manualFlagType)}
                    />
                ) : canFlagParts ? (
                    <Tooltip label={!part.flag ? 'Flag Part' : 'Unflag Part'} placement="top" hasArrow>
                        <IconButton
                            aria-label="Toggle flag"
                            icon={<Icon as={TbFlag as React.ElementType} w={5} h={5} fill={part.flag ? 'currentColor' : 'none'} />}
                            size="sm"
                            variant="ghost"
                            color={part.flag ? 'orange.500' : 'gray.400'}
                            _hover={{
                                bg: 'transparent',
                                color: part.flag ? 'orange.600' : 'orange.400',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onFlagToggle(part._id);
                            }}
                            isDisabled={part.excludeQuoting}
                        />
                    </Tooltip>
                ) : null}
                {canFlagParts && (part.assemblyColor || (part.assemblySubParts && part.assemblySubParts.length > 0)) && (
                    <AssemblyIndicator
                        part={part}
                        orderId={orderId}
                        refetch={refetch}
                    />
                )}
                {canIncludeExclude && (part.quoteAndExclude || (part.partNumber && part.excludeQuoting)) && (
                    <Tooltip
                        label={part.excludeQuoting
                            ? 'This part will not be quoted at all. Click to quote it and only exclude it from ordering.'
                            : 'Excluded from ordering, but still quoted. Click to stop quoting it entirely.'}
                        placement="top"
                        hasArrow
                    >
                        <YaxleIconButton
                            aria-label={part.excludeQuoting ? 'Quote this part' : 'Stop quoting this part'}
                            icon={<YaxleIcon name={part.excludeQuoting ? 'quoteOff' : 'quote'} size="sm" />}
                            size="sm"
                            color={part.excludeQuoting ? 'error.fg' : 'fg.default'}
                            _hover={{
                                bg: 'transparent',
                                color: part.excludeQuoting ? 'error.fg' : 'fg.muted',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onQuotingExclusionToggle(part);
                            }}
                        />
                    </Tooltip>
                )}
            </HStack>
        </Td>}
        <Td py={4}>
            <HStack spacing={2}>
                <Text fontWeight="medium" color="gray.900">
                    {index + 1}
                </Text>
                {part.isDuplicate && (
                    <Tooltip label="Duplicate part" placement="top" hasArrow>
                        <Badge
                            display="flex"
                            alignItems="center"
                            gap={1}
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            bg="orange.100"
                            color="orange.700"
                            fontSize="xs"
                            fontWeight="medium"
                        >
                            DUP
                        </Badge>
                    </Tooltip>
                )}
            </HStack>
        </Td>
        <Td py={4}>
            <Text color="gray.900">
                {part.quantity || 1}
            </Text>
        </Td>
        <Td py={4}>
            {part.type ?
                <PartType
                    partType={part.type}
                /> : null
            }
        </Td>
        <Td py={4}>
            <Text color="gray.900" fontWeight="medium">
                {part.name}
            </Text>
        </Td>
        <Td py={4}>
            <PartNumber
                partNumber={part.partNumber}
                vin={vin}
            />
        </Td>
        <Td py={4} textAlign="right">
            <Text color="gray.900" fontWeight="medium">
                {formatPrice(part.quotedListPrice)}
            </Text>
        </Td>
    </Tr>
);

interface NewPartsTableProps {
    parts: Array<QuotePart>;
    groups?: Array<AssemblyGroup>;
    vin: string;
    orderId: string;
    allPartsExcluded: boolean;
    somePartsExcluded: boolean;
    isBulkTogglePending: boolean;
    onExcludeAllToggle: () => void;
    onExclusionToggle: (part: QuotePart) => void;
    onQuotingExclusionToggle: (part: QuotePart) => void;
    onFlagToggle: (partId: string, reason?: string, manualFlagType?: ManualFlagType) => void;
    refetch: () => void | Promise<unknown>;
}

const NewPartsTable = ({
    parts,
    groups,
    vin,
    orderId,
    allPartsExcluded,
    somePartsExcluded,
    isBulkTogglePending,
    onExcludeAllToggle,
    onExclusionToggle,
    onQuotingExclusionToggle,
    onFlagToggle,
    refetch,
}: NewPartsTableProps) => {
    const isGrouped = !!groups;
    const isEmpty = isGrouped ? groups.length === 0 : parts.length === 0;

    const sessionContext = useContext(SessionContext);
    const canFlagParts = sessionContext?.permissions?.includes('toggle-flag');
    const canIncludeExclude = sessionContext?.permissions?.includes('include-exclude-parts');

    let rowIndex = 0;

    return (
        <Box overflowX="auto">
            <Table variant="simple" size="sm" w="100%" minW="800px" sx={{ 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}>
                <Thead>
                    <Tr bg="gray.50">
                        {canIncludeExclude && <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} minW="120px">
                            <HStack spacing={2}>
                                <Tooltip
                                    label={allPartsExcluded ? 'Include All Parts' : 'Exclude All Parts'}
                                    placement="top"
                                    hasArrow
                                >
                                    <Box display="inline-block">
                                        <Checkbox
                                            isChecked={allPartsExcluded}
                                            isIndeterminate={somePartsExcluded && !allPartsExcluded}
                                            onChange={onExcludeAllToggle}
                                            isDisabled={parts.length === 0 || isBulkTogglePending}
                                            size="md"
                                            aria-label={allPartsExcluded ? 'Include all parts' : 'Exclude all parts'}
                                            sx={{
                                                '&[data-checked]': {
                                                    bg: '#0071E3',
                                                    borderColor: '#0071E3',
                                                },
                                                '&[data-checked]:hover': {
                                                    bg: '#005BC4',
                                                    borderColor: '#005BC4',
                                                },
                                                '&[data-indeterminate]': {
                                                    bg: '#0071E3',
                                                    borderColor: '#0071E3',
                                                },
                                                '&[data-indeterminate]:hover': {
                                                    bg: '#005BC4',
                                                    borderColor: '#005BC4',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Tooltip>
                                <Text>EXCLUDE</Text>
                            </HStack>
                        </Th>}
                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="70px">
                            LINE #
                        </Th>
                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="60px">
                            QTY
                        </Th>
                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="100px">
                            PART TYPE
                        </Th>
                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="200px">
                            DESCRIPTION
                        </Th>
                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="140px">
                            OE PART #
                        </Th>
                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="100px" textAlign="right">
                            PRICE
                        </Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {isEmpty ? (
                        <Tr>
                            <Td colSpan={7} py={12} textAlign="center">
                                <Text fontSize="lg" color="gray.600" fontWeight="medium">
                                    Horray! There are no new parts to request at this time.
                                </Text>
                            </Td>
                        </Tr>
                    ) : isGrouped ? (
                        groups.map((group) => (
                            <React.Fragment key={group.parent._id}>
                                <Tr bg="gray.50">
                                    <Td colSpan={7} py={2.5} px={4}>
                                        <HStack spacing={2}>
                                            <Box
                                                w="10px"
                                                h="10px"
                                                borderRadius="full"
                                                bg={group.parent.assemblyColor || 'gray.500'}
                                                flexShrink={0}
                                            />
                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                Assembly: {group.parent.name}
                                            </Text>
                                            <Flex
                                                bg="gray.100"
                                                borderRadius="lg"
                                                h="18px"
                                                minW="18px"
                                                px="5px"
                                                align="center"
                                                justify="center"
                                                flexShrink={0}
                                            >
                                                <Text fontSize="11px" fontWeight="bold" lineHeight="1" color="gray.800">
                                                    {group.parts.length}
                                                </Text>
                                            </Flex>
                                        </HStack>
                                    </Td>
                                </Tr>
                                {group.parts.map((part) => {
                                    const index = rowIndex;
                                    rowIndex += 1;
                                    return (
                                        <NewPartsPartRow
                                            key={part._id}
                                            part={part}
                                            index={index}
                                            vin={vin}
                                            orderId={orderId}
                                            canFlagParts={!!canFlagParts}
                                            canIncludeExclude={!!canIncludeExclude}
                                            onExclusionToggle={onExclusionToggle}
                                            onQuotingExclusionToggle={onQuotingExclusionToggle}
                                            onFlagToggle={onFlagToggle}
                                            refetch={refetch}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))
                    ) : (
                        parts.map((part, index) => (
                            <NewPartsPartRow
                                key={part._id}
                                part={part}
                                index={index}
                                vin={vin}
                                orderId={orderId}
                                canFlagParts={!!canFlagParts}
                                canIncludeExclude={!!canIncludeExclude}
                                onExclusionToggle={onExclusionToggle}
                                onQuotingExclusionToggle={onQuotingExclusionToggle}
                                onFlagToggle={onFlagToggle}
                                refetch={refetch}
                            />
                        ))
                    )}
                </Tbody>
            </Table>
        </Box>
    );
};

interface AssemblyIndicatorProps {
    part: QuotePart;
    orderId: string;
    refetch: () => void | Promise<unknown>;
}

const AssemblyIndicator = ({ part, orderId, refetch }: AssemblyIndicatorProps) => {
    const toast = useToast();
    const deleteAssemblyMapping = useDeleteAssemblyMapping();
    const isParent = !!part.assemblySubParts && part.assemblySubParts.length > 0;
    const color = part.assemblyColor || 'gray.500';

    const handleDelete = (closePopover: () => void) => {
        deleteAssemblyMapping.mutate(
            { orderId, parentPartId: part._id },
            {
                onSuccess: () => {
                    refetch();
                    closePopover();
                    toast({
                        title: 'Assembly mapping deleted',
                        status: 'success',
                        duration: 4000,
                        isClosable: true,
                    });
                },
                onError: () => {
                    toast({
                        title: 'Failed to delete assembly mapping',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            }
        );
    };

    if (!isParent) {
        return (
            <Tooltip label="Part of an assembly" placement="top" hasArrow>
                <Box as="span" display="inline-flex" alignItems="center">
                    <Icon as={TbStack2 as React.ElementType} w={4} h={4} color={color} />
                </Box>
            </Tooltip>
        );
    }

    return (
        <Popover trigger="hover" placement="top">
            {({ onClose: closePopover }) => (
                <>
                    <PopoverTrigger>
                        <Box as="span" display="inline-flex" alignItems="center" cursor="pointer">
                            <Icon as={TbStack2 as React.ElementType} w={4} h={4} color={color} />
                        </Box>
                    </PopoverTrigger>
                    <Portal>
                        <PopoverContent width="240px" bg="white" boxShadow="none">
                            <PopoverBody>
                                <VStack align="stretch" spacing={2}>
                                    <Text fontSize="sm" color="gray.700">
                                        This part is the parent of an assembly mapping.
                                    </Text>
                                    <Button
                                        size="sm"
                                        colorScheme="red"
                                        variant="outline"
                                        leftIcon={<Icon as={TbTrash as React.ElementType} w={4} h={4} />}
                                        isLoading={deleteAssemblyMapping.isPending}
                                        onClick={() => handleDelete(closePopover)}
                                    >
                                        Delete Assembly Mapping
                                    </Button>
                                </VStack>
                            </PopoverBody>
                        </PopoverContent>
                    </Portal>
                </>
            )}
        </Popover>
    );
};

interface AssemblyMappingModalProps {
    isOpen: boolean;
    onClose: () => void;
    parts: Array<QuotePart>;
    orderId: string;
    refetch: () => void | Promise<unknown>;
}

const AssemblyMappingModal = ({ isOpen, onClose, parts, orderId, refetch }: AssemblyMappingModalProps) => {
    const toast = useToast();
    const createAssemblyMapping = useCreateAssemblyMapping();
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    // Parts already linked into a mapping during this session, so they don't get reused in another assembly.
    const [usedPartIds, setUsedPartIds] = useState<Set<string>>(new Set());
    const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
    const [parentId, setParentId] = useState<string | null>(null);

    const resetSelection = () => {
        setSelectedIds([]);
        setParentId(null);
    };

    const handleClose = () => {
        resetSelection();
        setUsedPartIds(new Set());
        onClose();
    };

    const availableParts = useMemo(
        () => parts.filter((p) => !usedPartIds.has(p._id) && !p.assemblyColor),
        [parts, usedPartIds]
    );

    const toggleSelect = (partId: string) => {
        setSelectedIds((prev) => {
            if (prev.includes(partId)) {
                const next = prev.filter((id) => id !== partId);
                if (parentId === partId) {
                    setParentId(next[0] ?? null);
                }
                return next;
            }
            if (prev.length === 0) {
                setParentId(partId);
            }
            return [...prev, partId];
        });
    };

    const canSave = parentId !== null && selectedIds.length >= 2 && !createAssemblyMapping.isPending;

    const handleSave = () => {
        if (parentId === null || selectedIds.length < 2) return;

        const subPartIds = selectedIds.filter((id) => id !== parentId);
        const mappedIds = [...selectedIds];

        createAssemblyMapping.mutate(
            { orderId, parentPartId: parentId, subPartIds },
            {
                onSuccess: () => {
                    setUsedPartIds((prev) => {
                        const next = new Set(prev);
                        mappedIds.forEach((id) => next.add(id));
                        return next;
                    });
                    resetSelection();
                    refetch();
                    toast({
                        title: 'Assembly mapping saved',
                        description: 'Select another assembly or close when finished.',
                        status: 'success',
                        duration: 4000,
                        isClosable: true,
                    });
                },
                onError: () => {
                    toast({
                        title: 'Failed to save assembly mapping',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            }
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader
                    fontSize="md"
                    fontWeight="semibold"
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.300"
                >
                    Map Assembly Parts
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody ref={bodyRef} minH={0}>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                        Select the parts that make up an assembly, then choose which one is the parent. Save to link them, then map another assembly if needed.
                    </Text>
                    {availableParts.length === 0 ? (
                        <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription fontSize="sm">No parts available to map.</AlertDescription>
                        </Alert>
                    ) : (
                        <VStack align="stretch" spacing={2}>
                            {availableParts.map((part) => {
                                const isSelected = selectedIds.includes(part._id);
                                return (
                                    <Flex
                                        key={part._id}
                                        align="center"
                                        justify="space-between"
                                        p={3}
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                        bg={isSelected ? '#E6F1FD' : 'white'}
                                        _hover={{ borderColor: isSelected ? '#0071E3' : 'gray.300' }}
                                    >
                                        <HStack spacing={3} align="center" flex={1} minW={0}>
                                            <Checkbox
                                                isChecked={isSelected}
                                                onChange={() => toggleSelect(part._id)}
                                                sx={{
                                                    '&[data-checked]': { bg: '#0071E3', borderColor: '#0071E3' },
                                                }}
                                            />
                                            <Box minW={0}>
                                                <Text fontSize="sm" fontWeight="medium" color="gray.900" noOfLines={1}>
                                                    {part.name}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                    {part.partNumber || 'No part number'}
                                                </Text>
                                            </Box>
                                        </HStack>
                                        {isSelected && (
                                            <Radio
                                                isChecked={parentId === part._id}
                                                onChange={() => setParentId(part._id)}
                                                colorScheme="blue"
                                            >
                                                <Text fontSize="xs" color="gray.600">Parent</Text>
                                            </Radio>
                                        )}
                                    </Flex>
                                );
                            })}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="action" variant="outlineGray" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        size="action"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3141a5' }}
                        onClick={handleSave}
                        isDisabled={!canSave}
                        isLoading={createAssemblyMapping.isPending}
                    >
                        Save Mapping
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NewPartsSection;
